import { z } from 'zod'

// Remplir un formulaire reçu en pièce (cerfa, demande d'un organisme, photo d'un courrier) :
// les champs du PDF quand il en a, sinon du texte posé par-dessus. L'assistant prépare une
// version remplie, nouvelle pièce liée à l'originale, qu'un humain relit puis valide (N1).
// Signer reste un geste humain (N2) : aucun champ de signature n'est rempli, aucune image
// n'est posée. Positions en fraction de la page telle qu'on la voit, depuis le haut à gauche.

export const TYPES_CHAMP = [
  'texte',
  'case',
  'choix',
  'radio',
  'liste',
  'bouton',
  'signature',
] as const
export type TypeChamp = (typeof TYPES_CHAMP)[number]

/** Zone d'un champ sur la page affichée, en fraction de la page (haut à gauche). */
export type Zone = { x: number; y: number; largeur: number; hauteur: number }

export type ChampFormulaire = {
  nom: string
  type: TypeChamp
  valeur: string | boolean | null
  options: string[]
  /** Page (à partir de 1) de son premier emplacement, s'il est connu. */
  page: number | null
  zone: Zone | null
  lectureSeule: boolean
  longueurMax: number | null
}

/** Ce qu'on lit d'un PDF avant de le remplir. Pages : dimensions affichées, en points. */
export type AnalyseFormulaire = {
  pages: { largeur: number; hauteur: number; rotation: number }[]
  champs: ChampFormulaire[]
  /** Formulaire XFA (champs dynamiques) : seul le texte posé par-dessus est possible. */
  xfa: boolean
  chiffre: boolean
}

const fraction = z.number().min(0).max(1)
const page = z.number().int().min(1).max(500)
const taille = z.number().min(5).max(24)

export const remplissageFormulaire = z
  .object({
    champs: z
      .array(
        z.object({
          nom: z.string().min(1).max(200),
          valeur: z.union([z.string().max(2000), z.boolean()]),
        }),
      )
      .max(300)
      .default([]),
    textes: z
      .array(
        z.object({
          page,
          x: fraction,
          y: fraction,
          texte: z.string().min(1).max(500),
          taille: taille.optional(),
        }),
      )
      .max(300)
      .default([]),
    coches: z
      .array(z.object({ page, x: fraction, y: fraction, taille: taille.optional() }))
      .max(100)
      .default([]),
  })
  .refine((d) => d.champs.length + d.textes.length + d.coches.length > 0, {
    message: 'Au moins un champ, un texte ou une coche.',
  })
export type RemplissageFormulaire = z.infer<typeof remplissageFormulaire>

/** Taille du texte posé par défaut, en points. */
export const TAILLE_TEXTE = 10

export type PlanRemplissage = {
  champs: { nom: string; type: TypeChamp; valeur: string | boolean }[]
  textes: { page: number; x: number; y: number; texte: string; taille: number }[]
  coches: { page: number; x: number; y: number; taille: number }[]
}

/** Un champ qui sert à signer : par sa nature, ou par son nom. */
export const estChampSignature = (c: { nom: string; type: TypeChamp }) =>
  c.type === 'signature' || /signat/i.test(c.nom)

const memeTexte = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' }) === 0

/**
 * Vérifie la demande contre le formulaire lu : champs connus, modifiables, valeurs du bon
 * type, pages existantes ; jamais de signature. Toutes les erreurs d'un coup.
 */
export function planRemplissage(
  analyse: AnalyseFormulaire,
  demande: RemplissageFormulaire,
): { plan: PlanRemplissage } | { erreurs: string[] } {
  if (analyse.chiffre) return { erreurs: ['PDF protégé : le remplir à la main.'] }
  const erreurs: string[] = []
  const plan: PlanRemplissage = { champs: [], textes: [], coches: [] }
  const vus = new Set<string>()
  for (const d of demande.champs) {
    const c = analyse.champs.find((x) => x.nom === d.nom)
    if (vus.has(d.nom)) {
      erreurs.push(`${d.nom} : donné deux fois.`)
      continue
    }
    vus.add(d.nom)
    if (!c) {
      erreurs.push(`${d.nom} : champ absent du formulaire.`)
      continue
    }
    if (estChampSignature(c)) {
      erreurs.push(`${d.nom} : signer reste un geste humain (N2), laisser vide.`)
      continue
    }
    if (c.type === 'bouton') {
      erreurs.push(`${d.nom} : bouton, rien à remplir.`)
      continue
    }
    if (c.lectureSeule) {
      erreurs.push(`${d.nom} : champ en lecture seule.`)
      continue
    }
    if (c.type === 'case') {
      if (typeof d.valeur !== 'boolean')
        erreurs.push(`${d.nom} : case à cocher, valeur true ou false.`)
      else plan.champs.push({ nom: c.nom, type: c.type, valeur: d.valeur })
      continue
    }
    if (typeof d.valeur !== 'string') {
      erreurs.push(`${d.nom} : valeur texte attendue.`)
      continue
    }
    if (c.type === 'texte') {
      if (c.longueurMax !== null && d.valeur.length > c.longueurMax)
        erreurs.push(`${d.nom} : ${c.longueurMax} caractères au plus.`)
      else plan.champs.push({ nom: c.nom, type: c.type, valeur: d.valeur })
      continue
    }
    // Choix, liste, boutons radio : une des options, retrouvée sans tenir compte de la casse.
    const option = c.options.find((o) => memeTexte(o, d.valeur as string))
    if (option === undefined) erreurs.push(`${d.nom} : valeur parmi ${c.options.join(', ')}.`)
    else plan.champs.push({ nom: c.nom, type: c.type, valeur: option })
  }
  const pages = analyse.pages.length
  const pageExiste = (n: number) => {
    if (n <= pages) return true
    erreurs.push(`Page ${n} : le document a ${pages} page${pages > 1 ? 's' : ''}.`)
    return false
  }
  for (const t of demande.textes)
    if (pageExiste(t.page)) plan.textes.push({ ...t, taille: t.taille ?? TAILLE_TEXTE })
  for (const c of demande.coches)
    if (pageExiste(c.page)) plan.coches.push({ ...c, taille: c.taille ?? TAILLE_TEXTE })
  return erreurs.length ? { erreurs: [...new Set(erreurs)] } : { plan }
}

/**
 * Boîte d'une page du PDF (non tournée, en points, origine en bas à gauche) et sa rotation
 * d'affichage (sens des aiguilles d'une montre, multiple de 90).
 */
export type BoitePage = { x: number; y: number; largeur: number; hauteur: number; rotation: number }

const quart = (r: number) => ((((Math.round(r / 90) * 90) % 360) + 360) % 360) as 0 | 90 | 180 | 270

/** Dimensions de la page telle qu'on la voit. */
export function dimensionsAffichees(b: BoitePage) {
  const r = quart(b.rotation)
  return r === 90 || r === 270
    ? { largeur: b.hauteur, hauteur: b.largeur }
    : { largeur: b.largeur, hauteur: b.hauteur }
}

/** Point de la page affichée (fractions, haut à gauche) vers les coordonnées du PDF. */
export function versPdf(b: BoitePage, u: number, v: number): { x: number; y: number } {
  const a = dimensionsAffichees(b)
  const pu = u * a.largeur
  const pv = v * a.hauteur
  const { largeur: W, hauteur: H } = b
  const r = quart(b.rotation)
  const [x, y] =
    r === 0 ? [pu, H - pv] : r === 90 ? [pv, pu] : r === 180 ? [W - pu, pv] : [W - pv, H - pu]
  return { x: b.x + x, y: b.y + y }
}

/** Point du PDF vers la page affichée (fractions, haut à gauche). */
export function depuisPdf(b: BoitePage, x: number, y: number): { u: number; v: number } {
  const a = dimensionsAffichees(b)
  const px = x - b.x
  const py = y - b.y
  const { largeur: W, hauteur: H } = b
  const r = quart(b.rotation)
  const [pu, pv] =
    r === 0 ? [px, H - py] : r === 90 ? [py, px] : r === 180 ? [W - px, py] : [H - py, W - px]
  return { u: pu / a.largeur, v: pv / a.hauteur }
}

/** Rotation du texte posé (degrés, sens inverse des aiguilles) pour qu'il se lise droit. */
export const rotationTexte = (b: BoitePage) => quart(b.rotation)

/** Nom de rangement de la version remplie : celui de l'originale, suffixé. */
export function nomVersionRemplie(nom: string): string {
  const point = nom.lastIndexOf('.')
  return point > 0 ? `${nom.slice(0, point)}_rempli${nom.slice(point)}` : `${nom}_rempli`
}
